import { FeedbackWithDetails } from '@/common/types/databaseTypes';
import {
    ActionIcon,
    Group,
    Menu,
    Paper,
    Stack,
    Text,
    UnstyledButton,
} from '@mantine/core';
import { IconDots } from '@tabler/icons-react';
import { formatDate } from '@tickitacka/shared';
import { useToggleFeedbackBookmark } from '../services/useFeedbackQueries';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useStackModalStore } from '@/common/stores/modalStore';
import UserAvatar from '@/common/components/UserAvatars/UserAvatar';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TAB_RECEIVED_FEEDBACK } from '@/common/constants/constants';
import { usePendingScrollStore } from '@/common/stores/pendingScrollStore';

export const BookmarkedFeedbackList = ({
    feedbackList,
}: {
    feedbackList: FeedbackWithDetails[];
}) => {
    const { t } = useTranslation('feedback');

    return (
        <Stack mb="md">
            <Text fw={600}>{t('bookmark.title')}</Text>

            {feedbackList.length === 0 && (
                <Paper withBorder radius="lg" px="md" py="lg" shadow="zero">
                    <Text
                        c="dimmed"
                        size="sm"
                        ta="center"
                        style={{ whiteSpace: 'pre-line' }}
                    >
                        {t('bookmark.placeHolder')}
                    </Text>
                </Paper>
            )}

            {feedbackList.map(feedback => (
                <FeedbackItem key={feedback.id} feedback={feedback} />
            ))}
        </Stack>
    );
};

const FeedbackItem = ({ feedback }: { feedback: FeedbackWithDetails }) => {
    const { t } = useTranslation(['common', 'feedback']);
    const toggleFeedbackBookmark = useToggleFeedbackBookmark();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const tabNameParam = searchParams.get('tab');
    const { setScrollTarget } = usePendingScrollStore();

    const handleToggleFeedbackBookmark = async () => {
        const notificationId = notifications.show({
            title: t('feedback:bookmark.remove.title'),
            message: t('common:notification.message'),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        });

        try {
            await toggleFeedbackBookmark.mutateAsync({
                feedback,
                isBookmarked: !feedback.is_bookmarked,
            });
            notifications.update({
                id: notificationId,
                title: t('feedback:bookmark.remove.title'),
                message: t('feedback:bookmark.remove.success'),
                loading: false,
                autoClose: 3000,
                color: 'green',
            });
        } catch (e) {
            notifications.update({
                id: notificationId,
                title: t('feedback:bookmark.remove.title'),
                message: t('feedback:bookmark.error'),
                color: 'red',
                loading: false,
                autoClose: 5000,
            });
        }
    };

    const handleBookmarkClick = () => {
        const modalId = useStackModalStore
            .getState()
            .openModal('CONFIRMATION', {
                title: t('feedback:bookmark.remove.title'),
                message: t('feedback:bookmark.remove.message'),
                onConfirm: () => {
                    useStackModalStore.getState().closeModal(modalId);
                    void handleToggleFeedbackBookmark();
                },
            });
    };

    return (
        <Paper radius="lg" px="md" py="lg" shadow="zero">
            {/* <Group justify="space-between" mb="sm">
                <Group>
                    {feedback.feedback_core_values
                        ?.sort((a, b) =>
                            a.is_positive === b.is_positive
                                ? 0
                                : a.is_positive
                                  ? -1
                                  : 1
                        )
                        .map(coreValue => (
                            <CoreValueBadge
                                key={coreValue.id}
                                label={coreValue.core_value?.name}
                                colorName={
                                    coreValue.is_positive ? 'blue' : 'red'
                                }
                                withIcon={true}
                            />
                        ))}
                </Group>
                <UnstyledButton onClick={handleBookmarkClick}>
                    <IconStarFilled
                        color={mantineTheme.colors.yellow[6]}
                        size={16}
                    />
                </UnstyledButton>
            </Group> */}

            {/* 투두 목록 */}
            {/* <TodoBadgeList
                feedbackTodos={feedback.feedback_todos}
                marginBottom="4px"
            /> */}

            {/* 보낸 사람 */}
            <Group justify="space-between" mb="sm">
                <Group gap={8}>
                    <UserAvatar
                        name={feedback.sender?.name}
                        imageUrl={feedback.sender?.profile_image_url}
                        size={20}
                    />
                    <Text fw={600}>{feedback.sender?.name}</Text>
                </Group>

                {/* 더보기 */}
                <Menu
                    shadow="md"
                    position="bottom-end"
                    onChange={opened => {
                        setIsMenuOpen(opened);
                    }}
                >
                    <Menu.Target>
                        <ActionIcon
                            size="md"
                            radius="md"
                            variant="subtle"
                            color="gray"
                        >
                            <IconDots size={14} />
                        </ActionIcon>
                    </Menu.Target>

                    <Menu.Dropdown>
                        {
                            <Menu.Item onClick={handleBookmarkClick}>
                                {t('feedback:action.removeBookmark')}
                            </Menu.Item>
                        }
                        {/* <Menu.Item color="red" onClick={handleBookmarkClick}>
                            {t('feedback:action.delete')}
                        </Menu.Item> */}
                    </Menu.Dropdown>
                </Menu>
            </Group>

            <UnstyledButton
                w="100%"
                onClick={() => {
                    // '받은 피드백' 탭으로 이동
                    if (tabNameParam !== TAB_RECEIVED_FEEDBACK) {
                        setSearchParams(
                            { tab: TAB_RECEIVED_FEEDBACK },
                            { replace: true }
                        );
                    }

                    // 스크롤 위치 조정 - 해당 피드백 위치로 이동
                    setScrollTarget({
                        type: 'feedback',
                        id: feedback.id,
                    });
                }}
            >
                {/* 피드백 내용 - 최대 3줄까지 보여줌 */}
                <Text mb="md" size="sm" lineClamp={3}>
                    {feedback.content}
                </Text>

                <Text ta="right" c="dimmed" size="xs">
                    {formatDate(new Date(feedback.register_date), {
                        showWeekday: false,
                        shortenMonth: true,
                    })}
                </Text>
            </UnstyledButton>
        </Paper>
    );
};
